/**
 * Admin API client — mirrors the mobile client shape (per CLAUDE.md):
 *   - single fetch wrapper
 *   - base URL configured once
 *   - Bearer token added automatically
 *   - 401 handled in one place (fires a callback, clears the token)
 * The token lives in localStorage — good enough for an internal tool.
 */
const API_URL = process.env.ADMIN_API_URL || 'http://localhost:3000';
const TOKEN_KEY = 'matematikly.admin.token';
export class ApiError extends Error {
    /**
     * `details` carries whatever structured extras the server attached to the
     * error body (currently: `line_errors` for the rule-set syntax check).
     * Any endpoint-specific type is asserted at the call site.
     */
    constructor(status, code, message, details = {}) {
        super(message ?? code);
        this.status = status;
        this.code = code;
        this.details = details;
        this.name = 'ApiError';
    }
}
let onUnauthorized = null;
export function setOnUnauthorized(cb) {
    onUnauthorized = cb;
}
/**
 * Called with every ApiError thrown by apiCall whose code isn't in the
 * caller's `handled` list. Wired up at boot (main.tsx) to notifyError so
 * business errors default to surfacing as a toast — no more silent 409s.
 * Kept as a callback (not a direct import) to avoid a circular dep between
 * the client and the antd-bound errors helper.
 */
let errorReporter = null;
export function setErrorReporter(cb) {
    errorReporter = cb;
}
/**
 * Decide whether an error should be auto-reported. Extracted so both apiCall
 * and any future test/harness can share the same rule. Silence on:
 *   - 401 unauthorized — the setOnUnauthorized handler owns the UX (redirect).
 *   - Any code the caller listed as `handled` — they'll display it themselves
 *     (e.g. RuleSetModal surfaces rule_syntax/unsatisfiable_rules inline).
 */
export function shouldSurfaceApiError(err, handled) {
    if (err.status === 401)
        return false;
    if (handled && handled.includes(err.code))
        return false;
    return true;
}
export function loadToken() {
    return localStorage.getItem(TOKEN_KEY);
}
export function saveToken(t) {
    localStorage.setItem(TOKEN_KEY, t);
}
export function clearToken() {
    localStorage.removeItem(TOKEN_KEY);
}
/**
 * Multipart upload sibling of `apiCall`. Browser fetch builds the
 * correct Content-Type header (with the boundary) when we hand it a
 * FormData, so we must NOT set Content-Type ourselves. Everything else
 * (auth header, error surfacing, 401 handling, details forwarding) is
 * the same contract as apiCall.
 */
export async function apiUpload(path, formData, opts = {}) {
    const { handled } = opts;
    const headers = {};
    const token = loadToken();
    if (token)
        headers.Authorization = `Bearer ${token}`;
    let res;
    try {
        res = await fetch(`${API_URL}${path}`, { method: 'POST', headers, body: formData });
    }
    catch (err) {
        const apiErr = new ApiError(0, 'network', err.message);
        if (shouldSurfaceApiError(apiErr, handled))
            errorReporter?.(apiErr);
        throw apiErr;
    }
    const text = await res.text();
    let parsed = null;
    if (text) {
        try {
            parsed = JSON.parse(text);
        }
        catch {
            parsed = text;
        }
    }
    if (res.status === 401) {
        clearToken();
        onUnauthorized?.();
        const b = (parsed && typeof parsed === 'object' ? parsed : {});
        throw new ApiError(401, b.error ?? 'unauthorized', b.message);
    }
    if (!res.ok) {
        const b = (parsed && typeof parsed === 'object' ? parsed : {});
        const { error: _e, message: _m, ...details } = b;
        const apiErr = new ApiError(res.status, b.error ?? 'error', b.message, details);
        if (shouldSurfaceApiError(apiErr, handled))
            errorReporter?.(apiErr);
        throw apiErr;
    }
    return parsed;
}
export async function apiCall(path, opts = {}) {
    const { method = 'GET', body, auth = true, handled } = opts;
    const headers = { 'Content-Type': 'application/json' };
    if (auth) {
        const token = loadToken();
        if (token)
            headers.Authorization = `Bearer ${token}`;
    }
    let res;
    try {
        res = await fetch(`${API_URL}${path}`, {
            method,
            headers,
            body: body === undefined ? undefined : JSON.stringify(body),
        });
    }
    catch (err) {
        const apiErr = new ApiError(0, 'network', err.message);
        if (shouldSurfaceApiError(apiErr, handled))
            errorReporter?.(apiErr);
        throw apiErr;
    }
    const text = await res.text();
    let parsed = null;
    if (text) {
        try {
            parsed = JSON.parse(text);
        }
        catch {
            parsed = text;
        }
    }
    if (res.status === 401) {
        clearToken();
        onUnauthorized?.();
        const b = (parsed && typeof parsed === 'object' ? parsed : {});
        const apiErr = new ApiError(401, b.error ?? 'unauthorized', b.message);
        // 401 is intentionally NOT auto-surfaced — the onUnauthorized handler
        // owns the UX (redirect to login).
        throw apiErr;
    }
    if (!res.ok) {
        const b = (parsed && typeof parsed === 'object' ? parsed : {});
        // Extra structured fields on the body (like line_errors) are forwarded
        // verbatim via ApiError.details so callers can present them.
        const { error: _e, message: _m, ...details } = b;
        const apiErr = new ApiError(res.status, b.error ?? 'error', b.message, details);
        if (shouldSurfaceApiError(apiErr, handled))
            errorReporter?.(apiErr);
        throw apiErr;
    }
    return parsed;
}
